import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  /** Remounts the boundary (clearing the error) when this key changes — e.g. the
   *  current route path, so navigating away from a broken page recovers. */
  resetKey?: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * App-level error boundary (CLE-274). A render error inside a page used to
 * white-screen the whole SPA — most visibly when clicking a task navigated to a
 * route that threw. This catches the error, shows a friendly fallback with a
 * recovery action, and resets automatically when the route changes so the rest
 * of the app stays usable.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    // Clear the error when navigating to a different route.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div className="mx-auto max-w-xl p-8">
          <h1 className="text-xl font-semibold text-foreground">A apărut o eroare</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Pagina nu a putut fi afișată. Poți încerca din nou sau te poți întoarce la pagina
            anterioară.
          </p>
          <pre className="mt-3 overflow-x-auto rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
            {this.state.error.message}
          </pre>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={this.reset}
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Încearcă din nou
            </button>
            <button
              type="button"
              onClick={() => window.history.back()}
              className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
            >
              Înapoi
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
